import { ProductSlugs } from '@deque/billing-utils';
import {
  AutomationLanguage,
  JavaFramework,
  MobileFramework,
  WebJsFramework
} from '@deque/jazzband-utils';
import { SettingsSchemaGeneralAccessibilityStandardValueEnum } from '@deque/orgwide-settings-client';
import { type IssueType } from '@deque/ui-issues';
import cypress from './assets/images/cypress.svg';
import playwright from './assets/images/playwright.svg';
import puppeteer from './assets/images/puppeteer.svg';
import seleniumwebdriver from './assets/images/seleniumwebdriver.svg';
import webdriverio from './assets/images/webdriverio.svg';
import webdriverjs from './assets/images/webdriverjs.svg';

// Keep this for backward compatibility with the old automation names [Used all over the application].
// Please extend this but don't directly use this in the new code.
export const AUTOMATIONS = {
  CYPRESS: WebJsFramework.Cypress,
  WEBDRIVERJS: WebJsFramework.WebDriverJs,
  PLAYWRIGHT: WebJsFramework.Playwright,
  PLAYWRIGHT_TEST: WebJsFramework.PlaywrightTest,
  PUPPETEER: WebJsFramework.Puppeteer,
  WEBDRIVERIO: WebJsFramework.WebdriverIo,
  WEBDRIVERIOTESTRUNNER: WebJsFramework.WebdriverIoTestrunner
};

export const JS_FRAMEWORKS = {
  ...AUTOMATIONS,
  CYPRESS_COMPONENT: WebJsFramework.CypressComponent
};

export const JAVA_FRAMEWORKS = {
  WEBDRIVER: JavaFramework.SeleniumWebDriver,
  // Display value intentionally matches JS_FRAMEWORKS.PLAYWRIGHT ('Playwright').
  // Java+Playwright is disambiguated from JS+Playwright by LANGUAGE in the docs slug
  // (wa-java-playwright vs wa-js-playwright), NOT by this display string. Because the
  // value 'Playwright' already keys AUTOMATION_IMAGES and ProductDocsAutomationPlatforms
  // (via JS_FRAMEWORKS.PLAYWRIGHT), do NOT add new entries to those maps for Java.
  PLAYWRIGHT: JavaFramework.Playwright
};

export const FRAMEWORKS = {
  javascript: JS_FRAMEWORKS,
  java: JAVA_FRAMEWORKS,
  typescript: JS_FRAMEWORKS
};

export const MobileAutomationSupportedFrameworks = {
  Appium: MobileFramework.Appium,
  Espresso: MobileFramework.Espresso,
  UIAutomator: MobileFramework.UiAutomator,
  XCUITest: MobileFramework.XcuiTest
};

export type Automation = string;
export const AUTOMATION_IMAGES: Record<Automation, string> = {
  [JS_FRAMEWORKS.CYPRESS]: cypress,
  [JS_FRAMEWORKS.CYPRESS_COMPONENT]: cypress,
  [JS_FRAMEWORKS.PLAYWRIGHT]: playwright,
  [JS_FRAMEWORKS.PLAYWRIGHT_TEST]: playwright,
  [JS_FRAMEWORKS.PUPPETEER]: puppeteer,
  [JS_FRAMEWORKS.WEBDRIVERIO]: webdriverio,
  [JS_FRAMEWORKS.WEBDRIVERIOTESTRUNNER]: webdriverio,
  [JS_FRAMEWORKS.WEBDRIVERJS]: webdriverjs,
  [JAVA_FRAMEWORKS.WEBDRIVER]: seleniumwebdriver
};

export interface ProjectInfo {
  automation: Automation;
  name: string;
}

export interface SeverityCounts {
  totalCurrent: number;
  totalLast: number;
  numNew: number;
  numFixed: number;
}

export type SeveritiesType = 'critical' | 'serious' | 'moderate' | 'minor';
export type AdditionalFiltersType =
  | 'new_issues'
  | 'best-practice'
  | 'experimental';

export type PageType = 'git' | 'gitless' | 'branch';
export const ACCESSIBILITY_STANDARDS: SettingsSchemaGeneralAccessibilityStandardValueEnum[] =
  Object.values(SettingsSchemaGeneralAccessibilityStandardValueEnum);

export type Impact = SeveritiesType | AdditionalFiltersType;
export type IssueFilter =
  | SeveritiesType
  | AdditionalFiltersType
  | SettingsSchemaGeneralAccessibilityStandardValueEnum;

export type Impacts = ReadonlyArray<Impact | IssueFilter>;
export const IMPACTS: Impacts = ['critical', 'serious', 'moderate', 'minor'];

export const FILTER_TAGS: IssueFilter[] = [
  ...IMPACTS,
  'new_issues',
  'best-practice',
  'experimental'
];

export enum SourceAppType {
  // Refers to the package @axe-core/watcher
  Integration = ProductSlugs.axeDevToolsWatcher,
  Mobile = ProductSlugs.axeDevToolsMobile,
  // The source app "API" refers to the user utilising the Axe DevTools APIs and/or Axe DevTools CLI
  // to send data to Axe DevHub.
  Api = ProductSlugs.axeDevToolsHTML
}

export enum SourceAppName {
  Watcher = 'Axe Watcher',
  Mobile = 'Mobile SDKs or Appium Integration',
  MobileAnalyzer = 'Mobile Analyzer',
  Api = 'Web APIs and CLI'
}

export { AutomationLanguage as AutomationSupportedLanguages };

export const ProductDocsAutomationLanguages = {
  [AutomationLanguage.Javascript]: 'js',
  [AutomationLanguage.Typescript]: 'ts',
  [AutomationLanguage.Java]: 'java'
};

export const ProductDocsAutomationPlatforms = {
  [JS_FRAMEWORKS.CYPRESS]: 'cypress',
  [JS_FRAMEWORKS.CYPRESS_COMPONENT]: 'cypress-component',
  [JS_FRAMEWORKS.PLAYWRIGHT]: 'playwright',
  [JS_FRAMEWORKS.PLAYWRIGHT_TEST]: 'playwright-test',
  [JS_FRAMEWORKS.PUPPETEER]: 'puppeteer',
  [JS_FRAMEWORKS.WEBDRIVERIO]: 'wdio',
  [JS_FRAMEWORKS.WEBDRIVERIOTESTRUNNER]: 'wdio-testrunner',
  [JS_FRAMEWORKS.WEBDRIVERJS]: 'wdjs',
  [JAVA_FRAMEWORKS.WEBDRIVER]: 'selenium'
};

export const SESSION_TIMESTAMP_TEMPLATE = 'MM/DD/YYYY hh:mm:ss A';

/** A date time format to be used with date-fns. This produces the following format: "January 1, 2020 12:00 AM" */
export const LONG_DATE_TIME_FORMAT = 'MMMM D, YYYY h:mm A';

// Product documentation base URLs
const DEVELOPER_HUB_DOCS_VERSION = 2;
const DEVTOOLS_FOR_WEB_DOCS_VERSION = 4;
export const DEVELOPER_HUB_DOCS_BASE_URL = `https://docs.deque.com/developer-hub/${DEVELOPER_HUB_DOCS_VERSION}`;
export const WATCHER_DOCS_BASE_URL = `https://docs.deque.com/devtools-for-web/${DEVTOOLS_FOR_WEB_DOCS_VERSION}`;
export const WATCHER_DOCS_CORS_BASE_URL = `https://docs.deque.com/cors/devtools-for-web/${DEVTOOLS_FOR_WEB_DOCS_VERSION}`;

// Developer Hub doc page URLs (English locale)
export const AXE_DEVELOPER_HUB_DOCS_URL = `${DEVELOPER_HUB_DOCS_BASE_URL}/en/dh-welcome`;
export const AXE_DEVELOPER_HUB_PROJECT_SETUP_DOCS_URL = `${DEVELOPER_HUB_DOCS_BASE_URL}/en/dh-project-setup`;
export const AXE_DEVELOPER_HUB_TROUBLESHOOTING_DOCS_URL = `${DEVELOPER_HUB_DOCS_BASE_URL}/en/dh-troubleshooting`;
export const AXE_DEVELOPER_HUB_UNDERSTAND_RESULTS_DOCS_URL = `${DEVELOPER_HUB_DOCS_BASE_URL}/en/dh-understand-results/#pipeline-information`;

// Devtools for Web doc page URLs (English locale)
export const AXE_DEVTOOLS_FOR_WEB_HOME_DOCS_URL = `${WATCHER_DOCS_BASE_URL}/en/wa-home`;
export const API_CLI_DOCS_URL = `${WATCHER_DOCS_BASE_URL}/en/cli-devhub`;

// Devtools for Mobile doc page URLs (English locale)
// The unversioned path 301-redirects to the current published version.
export const MOBILE_DOCS_BASE_URL = 'https://docs.deque.com/devtools-mobile';
export const MOBILE_EXPORT_FORMATS_DOCS_URL = `${MOBILE_DOCS_BASE_URL}/export-formats#which-format-should-i-use`;

// ViewByMember dropdown selection types
export const SelectionType = {
  UNINITIALIZED: 'uninitialized',
  PIPELINE: 'pipeline',
  MEMBER: 'member',
  MEMBER_NO_DATA: 'member_no_data',
  UNKNOWN: 'unknown'
} as const;

export type ExportFormat = 'json' | 'csv';

export const MIN_OVERVIEW_POLLING_TIMEOUT_MS = 2000;
export const MAX_OVERVIEW_POLLING_TIMEOUT_MS = 1024000; // 17 minutes
export const DEFAULT_ITEMS_PER_PAGE = 5;

/** Query param keys for the "View runs from" filter, preserved across branches, commits, gitless, and issue navigation. */
export const COMMIT_FILTER_PARAM_KEYS = [
  'is_canonical_source',
  'created_by_user_api_key_id'
] as const;

export const ISSUE_FLYOUT_RULE_PARAM = 'issue_rule';
export const ISSUE_FLYOUT_NODE_PARAM = 'issue_node';
/** Pages the flyout through one element's group instead of one rule. */
export const ISSUE_FLYOUT_GROUP_PARAM = 'issue_group';

export const ISSUE_FILTER_URL_KEYS: Array<keyof IssueType> = [
  'ruleId',
  'impact',
  'scanId',
  'testUrl',
  'testFilePath'
];

/**
 * The rule drill-down's filter keys, per platform: exactly the facets that
 * page's own filter bar offers, which `filterControlsForView` narrows to Scan
 * name on mobile and Page info on web. A key the page has no control for must
 * not thin its rows — nor the flyout's node list, which resolves positions
 * against them — since nothing on screen could then clear it.
 */
export const RULE_ISSUES_FILTER_URL_KEYS_MOBILE: Array<keyof IssueType> = [
  'scanId'
];

export const RULE_ISSUES_FILTER_URL_KEYS_WEB: Array<keyof IssueType> = [
  'testUrl'
];

/**
 * The group page's filter keys, per platform: Rule and Impact plus the facet
 * `filterControlsForView` offers there — Scan name on mobile, Page info on
 * web — for the same reason as the rule drill-down's split above.
 */
export const GROUP_ISSUES_FILTER_URL_KEYS_MOBILE: Array<keyof IssueType> = [
  'ruleId',
  'impact',
  'scanId'
];

export const GROUP_ISSUES_FILTER_URL_KEYS_WEB: Array<keyof IssueType> = [
  'ruleId',
  'impact',
  'testUrl'
];

export const ISSUE_PAGE_FILTER_URL_KEYS: Array<keyof IssueType> = ['scanId'];
